import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import $ from 'jquery';
import { asyncGetHtmlWidthDangerously } from '../../../components/common/size_helpers';
import { formatDateToLocale } from 'javascripts/util/dateFns';
import Color from 'color';
import RelatedRecordsOpener from './related_records/related_records_opener';
import { relatedRecordTypes } from './related_records/related_records';
import { connect } from 'react-redux';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';

const RIGHT_PADDING_FOR_INFINITE_BARS = 16;
const PADDING_FOR_DEPENDENCY_LINKS_WHEN_LABEL_ON_RIGHT = 10;

class Bar extends React.Component {
  static displayName = 'TimelineControl.Bar';

  constructor(props) {
    super(props);
    this.state = {
      labelWidth: 0,
    };
  }

  componentDidMount() {
    const label = this.props.bar.label.replace(/^\s+|\s+$/g, '');
    this.getHtmlWidth(label).then(p => {
      this.setState({ labelWidth: p.width });
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.bar.label !== this.props.bar.label) {
      const label = this.props.bar.label.replace(/^\s+|\s+$/g, '');
      this.getHtmlWidth(label).then(p => {
        this.setState({ labelWidth: p.width });
      });
    }
  }

  isMilestoneTimeline() {
    // Will show durations that are missing a toDate as milestones
    return this.props.timelineType === 'milestone';
  }

  isUnbounded() {
    return !(this.toDate.isValid() || this.fromDate.isValid());
  }

  hasDates() {
    return this.toDate.isValid() || this.fromDate.isValid();
  }

  hasToDateOnly() {
    return this.toDate.isValid() && !this.fromDate.isValid();
  }

  hasFromDateOnly() {
    return !this.toDate.isValid() && this.fromDate.isValid();
  }

  needsDarkText(hex) {
    return Color('#' + hex).isLight();
  }

  // Thick bars can have the labels to the right of the bar. We need to leave
  // some extra space between the bar and the label if there are outgoing
  // dependency lines.
  roomForOutgoingDependencyLinks() {
    var hasOutgoingDependencyLinks = false;

    if (this.props.dependencyLinks) {
      this.props.dependencyLinks.forEach(link => {
        if (link.child_record_id === this.props.recordId) {
          hasOutgoingDependencyLinks = true;
        }
      });
    }

    return hasOutgoingDependencyLinks
      ? PADDING_FOR_DEPENDENCY_LINKS_WHEN_LABEL_ON_RIGHT
      : 0;
  }

  getHtmlWidth = htmlElement => {
    return asyncGetHtmlWidthDangerously(
      htmlElement,
      this.props.fontSize,
      'bar-label',
      500,
      '10000px'
    );
  };

  render() {
    let barLeft, barWidth, titleLabel;
    this.fromDate = moment.utc(this.props.bar.from);
    this.toDate = moment.utc(this.props.bar.to);

    const canShowRelatedRecords =
      this.props.recordType && this.props.recordType in relatedRecordTypes;
    const relatedRecordsOpener =
      !window.isPublishing && canShowRelatedRecords ? (
        <RelatedRecordsOpener
          bookmarkId={this.props.bookmarkId}
          recordId={this.props.recordId}
          recordType={this.props.recordType}
          recordEpicsEnabled={this.props.recordEpicsEnabled}
          visualizationType={this.isMilestoneTimeline() ? 'milestone' : 'bar'}
        />
      ) : null;

    const recordIsMilestone = this.props.bar.isMilestone;

    const { scale } = this.props;
    const { labelWidth } = this.state;

    if (this.isMilestoneTimeline() || recordIsMilestone) {
      const barCenter = this.props.thickBars ? 7 : 4; // -3 to center milestone dot in its day
      barLeft =
        scale.dateToOffset(this.fromDate) + scale.pixelsPerDay / 2 - barCenter;
      barWidth = this.props.thickBars ? 21 : 17;
      titleLabel = `${this.props.fromDateColumnName}: ${formatDateToLocale(
        this.fromDate
      )}`;
    } else if (!this.hasDates()) {
      barLeft = scale.dateToOffset(scale.fromDate);
      barWidth =
        $('.timeline-content-outer').width() - RIGHT_PADDING_FOR_INFINITE_BARS;
      titleLabel = 'No start date - No end date';
    } else if (this.hasToDateOnly()) {
      titleLabel = `No start date - ${
        this.props.toDateColumnName
      }: ${this.toDate.format('L')}`;
      barLeft = scale.dateToOffset(scale.fromDate);
      barWidth = scale.dateToOffsetEnd(this.toDate) - barLeft;
    } else if (this.hasFromDateOnly()) {
      titleLabel = `${this.props.fromDateColumnName}: ${formatDateToLocale(
        this.fromDate
      )} - No end date`;
      barLeft = scale.dateToOffset(this.fromDate);
      barWidth =
        scale.dateToOffsetEnd(scale.toDate) -
        barLeft -
        RIGHT_PADDING_FOR_INFINITE_BARS;
    } else {
      titleLabel = `${this.props.fromDateColumnName}: ${formatDateToLocale(
        this.fromDate
      )} - ${this.props.toDateColumnName}: ${formatDateToLocale(this.toDate)}`;
      barLeft = scale.dateToOffset(this.fromDate);
      if (this.fromDate > this.toDate) {
        barWidth = 10;
      } else {
        barWidth = scale.dateToOffsetEnd(this.toDate) - barLeft;
      }
    }

    barLeft = barLeft || 0;

    // Ensure bar width is never less than 10 pixels
    if (this.hasDates()) {
      barWidth = Math.max(barWidth - 1, 10);
    }

    const barClass = classNames({
      bar: true,
      milestone: this.isMilestoneTimeline() || recordIsMilestone,
      hasToDateOnly: this.hasToDateOnly(),
      hasFromDateOnly: this.hasFromDateOnly(),
      unboundStart: !this.hasDates() || this.hasToDateOnly(),
      unboundEnd: !this.hasDates() || this.hasFromDateOnly(),
      unboundBoth: !this.hasDates(),
    });

    const backgroundColor =
      this.props.barColor.indexOf('rgba') === 0
        ? this.props.barColor
        : `#${this.props.barColor}`;

    let barStyle = {
      left: barLeft,
      width: barWidth,
    };

    const key = `Bar-${this.props.barHash}`;

    let label = this.props.bar.label.replace(/^\s+|\s+$/g, '');
    if (this.props.thickBars) {
      // Remove icons from inside bars
      label = label.replace(/<i class=("|')icon.+><\/i>/, '');
    }

    const textWidth = this.getHtmlWidth(label);
    let transform = null;
    // Account for the related records icon when present. This will place both
    // label and icon to the left of the bar if we bump label to the left
    const relatedRecordsIconWidth = relatedRecordsOpener ? 26 : 0;
    // Give the label some extra padding to account for some custom fields
    // being rendered as pills with extra padding
    const extraPadding = 6;
    const barLabelWidth = labelWidth + extraPadding + relatedRecordsIconWidth;

    let barLabelLeft = barLeft;
    let bumpLabeLeft = barLabelLeft + barWidth + 5;

    if (this.props.thickBars) {
      // If the bar label is wider than the bar and there is not enough room to accommodate the bar label to the right of the bar, then move the bar label to the left of the bar.
      if (
        barLabelWidth > barWidth &&
        barLeft + barWidth + barLabelWidth > scale.totalPixelWidth()
      ) {
        bumpLabeLeft = barLabelLeft = barLabelLeft - barLabelWidth - 1;
      } else {
        bumpLabeLeft += this.roomForOutgoingDependencyLinks();
      }
    } else if (
      barWidth < 500 &&
      barLeft + 300 > scale.totalPixelWidth() &&
      barLeft + textWidth.width + 5 > scale.totalPixelWidth()
    ) {
      // If the bar is on the right side of the roadmap, then make sure you can see the label by shifting it to the left
      // If the bar is at least 500px wide, skip the shifting of the label. Do not shift the label more than 300px over.
      const diff = Math.min(
        300,
        barLeft + textWidth.width - scale.totalPixelWidth()
      );
      transform = `translateX(-${diff + 5}px)`;
    }

    const barLabelStyle = {
      left: barLabelLeft,
      transform,
      WebkitTransform: transform,
    };
    if (transform) barLabelStyle['width'] = barLabelWidth;

    const opts = {
      key,
      class: barClass,
      style: barStyle,
      'data-placement': 'bottom',
      'data-title': this.props.relatedRecordsOpen ? null : titleLabel,
    };

    const darkText = this.needsDarkText(this.props.barColor);

    const barLabelClass = classNames({
      'bar-label': true,
      'inside-bar': this.props.thickBars,
      'outside-bar': !this.props.thickBars,
      'dark-text': darkText,
    });

    const showAsMilestone = this.isMilestoneTimeline() || recordIsMilestone;

    // When progress is shown, then we show a colored bar set to the progress width. This bar slides on top of the "gutter" underneath (the gutter is a lighter color of the bar color).
    // When progress is not shown, then we force the progress to be 100%. This covers all of the "gutter" underneath, and so the bar is a solid color.
    const progress = this.props.showProgress ? this.props.bar.progress : 100;

    return (
      <div
        data-bar-left={barLeft}
        data-label-width={barLabelWidth}
        data-bar-width={barWidth}
      >
        {this.props.thickBars ? (
          <>
            <aha-roadmap-bar
              data-tooltip-opener
              {...opts}
              color={backgroundColor}
              progress={progress}
              start={this.fromDate.isValid() ? '' : null}
              end={this.toDate.isValid() ? '' : null}
              milestone={showAsMilestone ? '' : null}
              ref={this.props.barRef}
            >
              <div className={barLabelClass}>
                <div className='inner-bar-wrapper'>
                  <div
                    style={{
                      display: 'inline-flex',
                      gap: '0px',
                      flexFlow: 'row nowrap',
                      placeContent: `flex-start ${
                        this.isUnbounded() ? 'start' : 'space-between'
                      }`,
                      alignItems: 'baseline',
                    }}
                  >
                    <SanitizeInnerHTML html={label} element={<div />} />
                    {relatedRecordsOpener}
                  </div>
                </div>
              </div>
            </aha-roadmap-bar>

            <div
              className='bar-bump-label bar-label'
              style={{ left: bumpLabeLeft }}
              data-tooltip-opener
            >
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <SanitizeInnerHTML html={label} element={<div />} />

                {relatedRecordsOpener}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className={barLabelClass}
              style={barLabelStyle}
              data-tooltip-opener
            >
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <SanitizeInnerHTML html={label} element={<div />} />

                {relatedRecordsOpener}
              </div>
            </div>

            <aha-roadmap-bar
              {...opts}
              color={backgroundColor}
              progress={progress}
              start={this.fromDate.isValid() ? '' : null}
              end={this.toDate.isValid() ? '' : null}
              milestone={showAsMilestone ? '' : null}
              ref={this.props.barRef}
            />
          </>
        )}
      </div>
    );
  }
}

export default connect(function mapStateToProps(state) {
  const timelineData = state.get('timelineData').toJS();

  return {
    relatedRecordsOpen: state.get('relatedRecordsOpen'),
    dependencyLinks: timelineData.dependencyLinks,
  };
})(Bar);
